import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  OAuthProvider,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
  type UserCredential,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db, usingEmulators } from "../lib/firebase";
import { defaultPreferences } from "./types";

const CALENDAR_SCOPE = "https://www.googleapis.com/auth/calendar";
const TOKEN_KEY = "timo.google.accessToken";
const TOKEN_EXP_KEY = "timo.google.accessTokenExp";

export function currentUser() {
  return auth.currentUser;
}

export function onUser(cb: (user: User | null) => void) {
  return onAuthStateChanged(auth, cb);
}

/**
 * Google sign-in. When `withCalendar` is true we request calendar scope and keep the
 * short-lived access token in sessionStorage so the client can ask the server to sync.
 * V1 limitation: no refresh token; the user re-connects after ~1h if they want a fresh sync.
 */
/** True on phones, tablets and in-app browsers, where a popup often never comes back. */
function prefersRedirect() {
  const ua = navigator.userAgent;
  const inApp = /(FBAN|FBAV|Instagram|Line|WeChat|Twitter|LinkedInApp)/i.test(ua);
  const touchMac = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1; // iPadOS
  return inApp || touchMac || /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || window.innerWidth < 900;
}

/**
 * Sign in with Apple.
 *
 * Apple requires this wherever another social sign-in is offered in an iOS app, and some people
 * simply prefer it - it can hide their real address behind a relay. Same session, same workspace.
 */
export async function signInWithApple(redirect = false) {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  // Apple's sheet does not survive being embedded, so a full redirect is the dependable path.
  if (redirect || usingEmulators || prefersRedirect()) return signInWithRedirect(auth, provider);
  try {
    return await storeCredential(await signInWithPopup(auth, provider), false);
  } catch (err) {
    const code = (err as { code?: string }).code ?? "";
    if (code === "auth/popup-blocked" || code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      return signInWithRedirect(auth, provider);
    }
    throw err;
  }
}

/** `redirect` forces a full-page redirect: needed when there was no click on this page to open a popup from. */
export async function signInWithGoogle(withCalendar = false, redirect = false) {
  const provider = new GoogleAuthProvider();
  if (withCalendar) provider.addScope(CALENDAR_SCOPE);
  provider.setCustomParameters({ prompt: withCalendar ? "consent" : "select_account" });
  sessionStorage.setItem("timo.auth.pendingCalendar", String(withCalendar));
  // The emulator widget cannot complete a popup handshake in embedded browsers; redirect is reliable everywhere.
  if (usingEmulators) return signInWithRedirect(auth, provider);
  // Phones and in-app browsers lose or block the popup, which surfaces as popup-closed-by-user.
  // The sign-in domain is our own, so a full redirect is the dependable path there.
  if (redirect || prefersRedirect()) return signInWithRedirect(auth, provider);
  try {
    return await storeCredential(await signInWithPopup(auth, provider), withCalendar);
  } catch (err) {
    const code = (err as { code?: string }).code ?? "";
    if (code === "auth/popup-blocked" || code === "auth/operation-not-supported-in-this-environment" || code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") return signInWithRedirect(auth, provider);
    throw err;
  }
}

async function storeCredential(result: UserCredential, withCalendar: boolean) {
  const cred = GoogleAuthProvider.credentialFromResult(result);
  if (withCalendar && cred?.accessToken) {
    sessionStorage.setItem(TOKEN_KEY, cred.accessToken);
    sessionStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + 55 * 60_000));
  }
  await ensureWorkspace(result.user);
  return result.user;
}

/** Call once on boot to finish a redirect sign-in and keep its calendar token. */
export async function completeRedirectSignIn() {
  try {
    const result = await getRedirectResult(auth);
    if (result) await storeCredential(result, sessionStorage.getItem("timo.auth.pendingCalendar") === "true");
  } catch (err) {
    return (err as Error).message;
  }
  return null;
}

export function googleAccessToken(): string | null {
  const exp = Number(sessionStorage.getItem(TOKEN_EXP_KEY) || 0);
  if (Date.now() > exp) return null;
  return sessionStorage.getItem(TOKEN_KEY);
}

export async function signOut() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXP_KEY);
  await fbSignOut(auth);
}

/** Idempotent workspace bootstrap (W1-02). Safe to call on every sign-in. */
export async function ensureWorkspace(user: User) {
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await setDoc(ref, { lastSeenAt: serverTimestamp(), email: user.email ?? null, name: user.displayName ?? null }, { merge: true });
    return;
  }
  await setDoc(ref, {
    email: user.email ?? null,
    name: user.displayName ?? null,
    photo: user.photoURL ?? null,
    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
    preferences: defaultPreferences(),
  });
}

/** Emulator-only: sign in with a throwaway email account so local testing never needs an OAuth handshake. */
export async function devSignIn(email = "dev@timo.local", password = "timo-dev-password") {
  if (!usingEmulators) throw new Error("Dev sign-in is only available against the emulators");
  try {
    const r = await signInWithEmailAndPassword(auth, email, password);
    await ensureWorkspace(r.user);
    return r.user;
  } catch {
    const r = await createUserWithEmailAndPassword(auth, email, password);
    await ensureWorkspace(r.user);
    return r.user;
  }
}
